import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { DrawerService } from '@app-services/drawer.service';
import { EmailSearchAdvancedDialogComponent } from '../email-search-advanced-dialog/email-search-advanced-dialog.component';
import { SearchEvent } from '@app-types/search';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { MatIconButton } from '@angular/material/button';
import { Router } from '@angular/router';
import { LocalStorageService } from '@app-services/local-storage.service';
import { MAX_SEARCH, STORAGE_NAMES } from '@app-shared/constants/constants';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  OverlayModule,
} from '@angular/cdk/overlay';
import { NotificationPanelComponent } from '@app-components/notification-panel/notification-panel.component';
import { MatDialog } from '@angular/material/dialog';
import { Notification } from '@app-types/notification';
import { NOTIFICATIONS } from '@app-shared/constants/notifications';
import { NotificationDialogComponent } from '@app-components/notification-dialog/notification-dialog.component';
import { MatBadge } from '@angular/material/badge';
import { NgClass } from '@angular/common';

const { PRE_RELEASE } = STORAGE_NAMES;

@Component({
  selector: 'app-email-search-input',
  imports: [
    TranslateModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatIconModule,
    MatInputModule,
    EmailSearchAdvancedDialogComponent,
    MatIconButton,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    NotificationPanelComponent,
    OverlayModule,
    MatBadge,
    NgClass,
  ],
  templateUrl: './email-search-input.component.html',
  styleUrls: ['./email-search-input.component.scss'],
})
export class EmailSearchInputComponent implements OnInit {
  dialog = inject(MatDialog);
  @ViewChild('topInput', { static: false }) topInput: ElementRef;
  @Input() public fullWidth = false;
  @Input() public search = '';
  @Input() public hasFilters = false;
  @Input() public isDefaultSearchParams = true;
  @Output() public valueChangedEventEmitter = new EventEmitter<string>();
  @Output() public hideNotificationsEmitter = new EventEmitter<boolean>();
  @Output() public searchEventEmitter = new EventEmitter<SearchEvent>();
  @Output() public startTour = new EventEmitter();
  @ViewChild('advancedSearch') advancedSearch!: TemplateRef<any>;
  hasInputError = false;
  preRelease?: boolean;
  public isOpen = false;
  public isOpenDialogMessage = false;
  public notifications: Notification[] = [
    {
      id: 1,
      title: 'New PM3 version 3.0.0!',
      message:
        'A new version of PecManager3 is available. Refresh the page by pressing Ctrl + F5.',
      to: 'pm3.3.platfoza@pec.it',
      subject: 'Settings',
      attachments: ['Image.png', '1649908528162816443.eml', '123.xlsx'],
      html: JSON.stringify(
        {
          Status: 0,
          RejectedRecipients: [],
          ApprovedRecipients: ['pm3.3.platfoza@pec.it'],
          FromRejected: false,
          GeneralErrors: [],
          AllResponses: [
            {
              EnhancedStatusCode: '2.1.0',
              Code: 250,
              Lines: ['Ok'],
              Status: 0,
              Message: 'Ok',
            },
            {
              EnhancedStatusCode: '2.1.5',
              Code: 250,
              Lines: ['Ok'],
              Status: 0,
              Message: 'Ok',
            },
            {
              EnhancedStatusCode: '2.0.0',
              Code: 250,
              Lines: ['Ok: 1954 bytes queued'],
              Status: 0,
              Message: 'Ok: 1954 bytes queued',
            },
          ],
          RejectedRecipientsErrors: {},
        },
        null,
        2
      ),
      seen: false,
      time: new Date(),
    },
  ];
  private nextId = 0;

  constructor(
    private drawerService: DrawerService,
    public userWorkspaceService: UserWorkspaceService,
    protected router: Router,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.preRelease = this.localStorageService.getData(PRE_RELEASE);
    this.preRelease && this.sendNotification();
  }

  sendNotification(): void {
    setInterval(() => {
      if (!NOTIFICATIONS?.[this.nextId]) {
        this.nextId = 0;
      }
      const newMessage = {
        ...(NOTIFICATIONS[this.nextId] ?? ''),
        time: new Date(),
        id: new Date().getTime(),
      };
      this.notifications = [newMessage, ...this.notifications];
      this.nextId = this.nextId + 1;
    }, 5000);
  }

  closePanel(): void {
    this.isOpen = false;
  }

  onMarkAsRead(id: number): void {
    console.log(id);
    this.notifications = this.notifications.filter(el => el.id !== id);
  }

  onMarkAllAsRead(): void {
    this.notifications = [];
  }

  openDialogMessage(notification: Notification): void {
    this.isOpenDialogMessage = true;
    const dialogRef = this.dialog.open(NotificationDialogComponent, {
      data: {
        notification,
      },
      autoFocus: false,
      width: '1200px',
    });

    dialogRef.afterClosed().subscribe(() => {
      this.onMarkAsRead(notification.id);
      this.isOpenDialogMessage = false;
    });
  }

  onValueChanged($event: Event): void {
    const value = ($event.target as HTMLInputElement).value;
    this.search = value;
    (value.length >= MAX_SEARCH || !value) && (this.hasInputError = false);
    this.valueChangedEventEmitter.emit(value);
  }

  onHideNotificationsChanged($event: MatSlideToggleChange): void {
    const value = !$event.checked;
    if (!!this.search && this.search.length < MAX_SEARCH) {
      $event.source.checked = !$event.checked;
      this.hasInputError = true;
      return;
    }
    this.hideNotificationsEmitter.emit(value);
    if (this.search === null) {
      this.search = '';
      this.valueChangedEventEmitter.emit('');
    }
    this.searchEventEmitter.emit({ hideNotificationsEmitter: true });
  }

  onSearchBtnClicked(): void {
    if (!!this.search && this.search.length < MAX_SEARCH) {
      this.hasInputError = true;
      return;
    }
    if (this.search === null) {
      this.search = '';
      this.valueChangedEventEmitter.emit('');
    }
    this.searchEventEmitter.emit();
  }

  onXClicked(): void {
    this.search = '';
    this.hasInputError = false;
    this.valueChangedEventEmitter.emit('');
    this.searchEventEmitter.emit();
  }

  onSearchFilters(data: SearchEvent): void {
    const isSubjectError =
      data?.filter?.subject && data.filter.subject.length < MAX_SEARCH;
    const isBodyError =
      data?.filter?.body && data.filter.body.length < MAX_SEARCH;
    const isFromError =
      data?.filter?.from && data.filter.from.length < MAX_SEARCH;
    const isRecipientError =
      data?.filter?.to && data.filter.to.length < MAX_SEARCH;
    const isAttachmentNameError =
      data?.filter?.attachmentName &&
      data.filter.attachmentName.length < MAX_SEARCH;

    const isFiltersError =
      isSubjectError ||
      isBodyError ||
      isFromError ||
      isRecipientError ||
      isAttachmentNameError;

    if (isFiltersError) return;
    this.search = '';
    this.hasInputError = false;
    this.valueChangedEventEmitter.emit('');
    this.searchEventEmitter.emit(data);
    this.closeAdvancedSearch();
  }

  public get widthClass(): string {
    return this.fullWidth ? ' w-full' : ' w-40';
  }

  public openAdvancedSearch(): void {
    this.drawerService.openDrawer(this.advancedSearch);
  }

  public closeAdvancedSearch(): void {
    this.drawerService.closeDrawer();
    // Defer focus so the same Enter keyup does not trigger onSearchBtnClicked on topInput.
    setTimeout(() => this.topInput?.nativeElement?.focus());
  }

  isAnyFolder(): boolean {
    return (
      !!this.userWorkspaceService.mailAccounts?.length &&
      !!this.userWorkspaceService.userFolders?.length
    );
  }
}
